/* ─── Standards System ────────────────────────────────────────────────
 * UK Environment Agency  —  Australian EPA  —  EU UWWTD (greyed out)
 * Each standard defines discharge limits, compliance rules, report
 * formatting, and legal references. Switching standards updates all
 * limits, guidance text, and report output automatically.
 * ──────────────────────────────────────────────────────────────────── */

export type StandardCode = 'UK_EA' | 'AU_EPA' | 'SA_WATER' | 'EU_UWWTD' | 'US_EPA' | 'PL_WP';

export interface WastewaterStandard {
  code: StandardCode;
  name: string;
  shortName: string;
  country: string;
  flag: string; // emoji
  color: string; // tailwind color class
  bgColor: string;
  description: string;
  regulatoryBody: string;
  actReference: string;
  permitType: string;
  websiteUrl: string;
  available: boolean;
  comingSoonText?: string;

  // Discharge limits for this standard (mg/L unless noted)
  limits: {
    tss: number;
    bod5: number;
    cod: number;
    oilGrease: number;
    tds: number;
    tkn: number;
    totalNitrogen: number;
    totalPhosphorus: number;
    phMin: number;
    phMax: number;
    temperature: number; // °C max
    conductivity: number; // µS/cm max
    // metals
    lead: number;
    mercury: number;
    cadmium: number;
    chromium: number;
    arsenic: number;
    nickel: number;
    zinc: number;
    copper: number;
    // other
    ammoniaN: number;
    sulfides: number;
    phenol: number;
    surfactants: number;
    chlorides: number;
  };

  // SA Water / utility-specific excess concentration charges
  // Charges apply when annual discharge exceeds threshold volumes
  excessCharges?: {
    annualTrigger: string;  // e.g. ">10 t/yr BOD or SS, or >20 t/yr TDS, or >10,000 kL/yr"
    charges: Array<{
      parameter: string;
      condition: string;
      ratePerKg: number;    // AUD excl. GST
      notes?: string;
    }>;
  };

  // How compliance is evaluated
  complianceNotes: string[];

  // Report formatting
  report: {
    headerTitle: string;
    headerSubtitle: string;
    certificationMark: string;
    certificationText: string;
    legalReference: string;
    footerText: string;
    complianceStatement: string;
    dischargeStandardLabel: string;
  };

  // UI labels
  labels: {
    dischargeLimitsTitle: string;
    dischargeLimitsSubtitle: string;
    complianceTabLabel: string;
    standardBadge: string;
  };
}

/* ─── UK Environment Agency Standard ───────────────────────────────── */
export const UK_EA_STANDARD: WastewaterStandard = {
  code: 'UK_EA',
  name: 'UK Environment Agency',
  shortName: 'UK EA',
  country: 'United Kingdom',
  flag: '🇬🇧',
  color: 'text-blue-400',
  bgColor: 'bg-blue-700',
  description:
    'Environment Agency discharge permits under the Environmental Permitting (England and Wales) Regulations 2016 (EPR). Covers water discharge activities, groundwater activity permits, and trade effluent consent under the Water Industry Act 1991.',
  regulatoryBody: 'Environment Agency (England) / NRW (Wales) / SEPA (Scotland)',
  actReference: 'EPR 2016 / Water Industry Act 1991 / UWWTD 2024/3019 (retained EU law)',
  permitType: 'Water Discharge Activity Permit / Trade Effluent Consent',
  websiteUrl: 'https://www.gov.uk/guidance/control-of-activity-in-the-water-environment',
  available: true,

  limits: {
    // UWWTD 2024/3019 Table 1 (secondary treatment) — UK retained EU law
    tss: 35,          // Table 1: 35 mg/L (90% reduction)
    bod5: 25,         // Table 1: 25 mg/L O2 (70-90% reduction)
    cod: 125,         // Table 1: 125 mg/L O2 (75% reduction)
    oilGrease: 15,    // Typical EA permit condition for O&G
    tds: 2000,        // Site-specific; 2000 mg/L typical for inland waters
    tkn: 10,          // Typical EA permit for sensitive waters
    totalNitrogen: 10,// UWWTD 2024: 10 mg/L N (>100k PE), 15 mg/L (10k-100k PE)
    totalPhosphorus: 1,// UWWTD 2024: 1 mg/L P (>100k PE), 2 mg/L (10k-100k PE)
    phMin: 6.0,       // Standard pH range for UK surface water discharge
    phMax: 9.0,
    temperature: 30,  // Typically 30°C max for receiving water protection
    conductivity: 2500,// Site-specific; 2500 µS/cm typical
    // Metals — Environmental Quality Standards (EQS) Directive 2013/39/EU
    lead: 0.5,        // EQS annual average
    mercury: 0.005,   // EQS — very strict
    cadmium: 0.2,     // EQS annual average (hardness-dependent)
    chromium: 1.0,    // EQS total chromium
    arsenic: 0.5,     // EQS annual average
    nickel: 1.0,      // EQS annual average
    zinc: 5.0,        // EQS annual average (hardness-dependent)
    copper: 1.0,      // EQS annual average
    ammoniaN: 5.0,    // UWWTD: typically 5 mg/L N for salmonid waters
    sulfides: 1.0,    // Typical EA permit
    phenol: 0.5,      // EQS annual average
    surfactants: 2.0, // Typical EA permit for MBAS
    chlorides: 400,   // Site-specific; 400 mg/L typical
  },

  complianceNotes: [
    'Discharge to controlled waters requires EA water discharge permit',
    'Discharge to sewer requires trade effluent consent from water utility',
    'Groundwater discharge requires environmental permit',
    'Storm overflow discharges regulated under DWF (Dry Weather Flow) conditions',
    'Macro-plant nutrient assessment ( phosphorus ) required for sensitive water bodies',
    'Monthly composite sampling typically required for permit compliance',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Wastewater Assessment Report',
    headerSubtitle: 'Prepared in accordance with UK Environment Agency discharge permitting requirements',
    certificationMark: 'UKAS / MCERTS',
    certificationText: 'MCERTS-certified monitoring and analysis methods applied',
    legalReference: 'Environmental Permitting (England and Wales) Regulations 2016 (SI 2016/1154)',
    footerText: 'This assessment is for engineering planning purposes. Final discharge limits are subject to Environment Agency permit determination based on site-specific environmental risk assessment.',
    complianceStatement: 'Effluent quality evaluated against typical UK Environment Agency permit conditions for discharge to controlled waters. Site-specific permit conditions may differ.',
    dischargeStandardLabel: 'UK Environment Agency — Typical Permit Conditions',
  },

  labels: {
    dischargeLimitsTitle: 'UK Discharge Limits',
    dischargeLimitsSubtitle: 'Based on Environment Agency typical permit conditions (EPR 2016)',
    complianceTabLabel: 'UK Compliance',
    standardBadge: 'UK EA Standard',
  },
};

/* ─── Australian EPA Standard ──────────────────────────────────────── */
export const AU_EPA_STANDARD: WastewaterStandard = {
  code: 'AU_EPA',
  name: 'Australian EPA',
  shortName: 'AU EPA',
  country: 'Australia',
  flag: '🇦🇺',
  color: 'text-emerald-400',
  bgColor: 'bg-emerald-600',
  description:
    'Australian state EPA discharge standards under the National Water Quality Management Strategy (NWQMS) and Australian Guidelines for Water Recycling (AGWR). Covers EPA Victoria, NSW EPA, Queensland DES, SA EPA, and WA DWER licensing.',
  regulatoryBody: 'State EPA — Vic EPA / NSW EPA / Qld DES / SA EPA / WA DWER',
  actReference: 'ANZG (2018) toxicant DGVs / NWQMS / State EP Acts (Vic: EP Act 1970, NSW: POEO Act 1997)',
  permitType: 'Environment Protection Licence (EPL) / Works Approval',
  websiteUrl: 'https://www.waterquality.gov.au/',
  available: true,

  limits: {
    // Australian EPA — typical trade waste discharge limits
    // Based on Barwon Water / EPA Victoria typical licence conditions
    tss: 60,          // General trade waste: 60 mg/L (up to 600 accepted with treatment)
    bod5: 300,        // General trade waste: 300 mg/L (up to 600 accepted)
    cod: 500,         // General trade waste: 500 mg/L (up to 1000 accepted)
    oilGrease: 15,    // Typical EPA licence: 15 mg/L (max 30 mg/L)
    tds: 2000,        // kg/day limit; ~2000 mg/L typical concentration
    tkn: 100,         // General trade waste: 100 mg/L N
    totalNitrogen: 200, // General trade waste: 200 mg/L N
    totalPhosphorus: 10, // General trade waste: 10 mg/L P (up to 20 accepted)
    phMin: 6.0,       // EPA typical range: 6.0–10.0
    phMax: 10.0,
    temperature: 40,  // <40°C at point of discharge
    conductivity: 2500, // µS/cm — site-specific
    // Metals — ANZG (2018) toxicant default guideline values (DGVs)
    lead: 0.05,       // ANZG DGV for 99% species protection (freshwater)
    mercury: 0.001,   // ANZG DGV — extremely strict
    cadmium: 0.02,    // ANZG DGV (hardness-dependent)
    chromium: 0.5,    // ANZG DGV total Cr
    arsenic: 0.1,     // ANZG DGV
    nickel: 0.2,      // ANZG DGV (hardness-dependent)
    zinc: 0.5,        // ANZG DGV (hardness-dependent)
    copper: 0.02,     // ANZG DGV (hardness-dependent)
    ammoniaN: 100,    // General trade waste: 100 mg/L N (unionised NH3 <5 mg/L)
    sulfides: 1.0,    // Typical EPA condition
    phenol: 0.3,      // ANZG DGV
    surfactants: 5.0, // Typical EPA condition (MBAS)
    chlorides: 600,   // Site-specific; up to 600 mg/L common
  },

  complianceNotes: [
    'Discharge to waters requires EPA licence under state EP Act',
    'Discharge to sewer requires trade waste agreement with water authority',
    'Reuse / recycling governed by Australian Guidelines for Water Recycling (AGWR)',
    'Irrigation reuse requires setback distances and soil suitability assessment',
    'Nutrient limits tightened for discharge to sensitive receiving waters (e.g. Great Barrier Reef catchment)',
    'Weekly to monthly sampling typically required for EPL compliance reporting',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Wastewater Assessment Report',
    headerSubtitle: 'Prepared in accordance with Australian EPA Environment Protection Licence requirements',
    certificationMark: 'NATA',
    certificationText: 'NATA-accredited testing methods referenced where applicable',
    legalReference: 'National Water Quality Management Strategy (NWQMS) 2018, Australian Guidelines for Water Recycling (AGWR)',
    footerText: 'This assessment is for engineering planning purposes. Final discharge limits are subject to state EPA licence determination based on site-specific environmental risk assessment and receiving waters sensitivity.',
    complianceStatement: 'Effluent quality evaluated against typical Australian EPA licence conditions for discharge to waters. State-specific and site-specific conditions may differ.',
    dischargeStandardLabel: 'Australian EPA — Typical Licence Conditions',
  },

  labels: {
    dischargeLimitsTitle: 'Australian Discharge Limits',
    dischargeLimitsSubtitle: 'Based on typical Australian EPA licence conditions (NWQMS / AGWR)',
    complianceTabLabel: 'AU Compliance',
    standardBadge: 'AU EPA Standard',
  },
};

/* ─── SA Water Trade Waste Standard (South Australia only) ────────────────
 * Applies to trade waste discharge to SA Water sewerage network.
 * Source: SA Water Trade Waste Fees & Charges / Restricted Wastewater
 *         Acceptance Standards (RWAS) — sawater.com.au
 * Excess concentration charges apply once annual discharge exceeds:
 *   10 tonnes BOD or SS/yr  ·  20 tonnes TDS/yr  ·  10,000 kL/yr
 * ─────────────────────────────────────────────────────────────────────── */
export const SA_WATER_STANDARD: WastewaterStandard = {
  code: 'SA_WATER',
  name: 'SA Water — Trade Waste',
  shortName: 'SA Water',
  country: 'Australia (SA)',
  flag: '🇦🇺',
  color: 'text-blue-600',
  bgColor: 'bg-blue-600',
  description:
    'SA Water trade waste discharge conditions for connection to the SA Water sewerage network ' +
    'under a Trade Waste Agreement. Excess concentration charges apply when annual discharge exceeds ' +
    'volume thresholds. Source: SA Water Restricted Wastewater Acceptance Standards (RWAS) and ' +
    'SA Water Fees & Charges Schedule.',
  regulatoryBody: 'SA Water (South Australian Water Corporation)',
  actReference: 'Water Industry Act 2004 (SA) / SA Water Restricted Wastewater Acceptance Standards (RWAS)',
  permitType: 'Trade Waste Agreement (TWA) / Permit',
  websiteUrl: 'https://www.sawater.com.au/business-and-industry/trade-waste',
  available: true,

  limits: {
    // SA Water RWAS general acceptance limits for trade waste to sewer
    tss:             500,   // ≤500 mg/L — excess charges above this concentration
    bod5:            1000,  // Excess charge rate doubles above 1000 mg/L ($0.251 → $0.378/kg)
    cod:             2000,  // Approx 2× BOD5 limit; site-specific
    oilGrease:       100,   // ≤100 mg/L — strictly enforced
    tds:             1500,  // ≤1500 mg/L general limit; excess charges apply
    tkn:             130,   // ≤130 mg/L N; excess charges above 650 mg/L ($0.392/kg)
    totalNitrogen:   130,   // same as TKN for trade waste context
    totalPhosphorus: 15,    // ≤15 mg/L P; excess charges $1.908/kg all concentrations
    phMin:           6.0,   // pH 6–10 — trade waste acceptance range
    phMax:           10.0,
    temperature:     38,    // ≤38°C at point of discharge
    conductivity:    2500,  // Site-specific; indicative limit
    // Metals — SA Water RWAS limits for trade waste to sewer
    lead:     0.5,
    mercury:  0.005,
    cadmium:  0.02,
    chromium: 1.0,
    arsenic:  0.5,
    nickel:   1.0,
    zinc:     5.0,
    copper:   1.0,
    ammoniaN: 100,
    sulfides: 1.0,
    phenol:   0.5,
    surfactants: 5.0,
    chlorides:   600,
  },

  excessCharges: {
    annualTrigger: 'Charges apply when annual discharge exceeds 10 t BOD or SS, 20 t TDS, or 10,000 kL volume',
    charges: [
      { parameter: 'BOD',  condition: '≤1,000 mg/L',  ratePerKg: 0.251, notes: 'Per kg of BOD discharged' },
      { parameter: 'BOD',  condition: '>1,000 mg/L',  ratePerKg: 0.378, notes: 'Higher rate for high-strength waste' },
      { parameter: 'SS',   condition: '≤500 mg/L',    ratePerKg: 0.218, notes: 'Per kg of suspended solids' },
      { parameter: 'SS',   condition: '>500 mg/L',    ratePerKg: 0.315, notes: 'Higher rate above acceptance limit' },
      { parameter: 'TDS',  condition: 'All concentrations', ratePerKg: 0.126, notes: 'Per kg of dissolved solids' },
      { parameter: 'TKN',  condition: '>650 mg/L',    ratePerKg: 0.392, notes: 'Total Kjeldahl Nitrogen' },
      { parameter: 'TP',   condition: 'All concentrations', ratePerKg: 1.908, notes: 'Total Phosphorus — highest charge rate' },
    ],
  },

  complianceNotes: [
    'Trade Waste Agreement (TWA) required before discharge to SA Water sewer network',
    'Discharge must comply with SA Water Restricted Wastewater Acceptance Standards (RWAS)',
    'Excess concentration charges apply when annual volume triggers are exceeded',
    'pH must remain within 6.0–10.0 at all times — instantaneous limit',
    'Temperature must not exceed 38°C at the point of discharge',
    'Grease/oil ≤100 mg/L — grease trap or DAF required for food industry',
    'Monthly NATA-accredited sampling recommended; quarterly minimum for TWA compliance',
    'SA Water may require pre-treatment equipment upgrade if limits are consistently exceeded',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Trade Waste Assessment Report',
    headerSubtitle: 'Prepared in accordance with SA Water Trade Waste Agreement requirements',
    certificationMark: 'NATA',
    certificationText: 'NATA-accredited laboratory analysis methods applied (AWQC or equivalent)',
    legalReference: 'Water Industry Act 2004 (SA) / SA Water Restricted Wastewater Acceptance Standards',
    footerText: 'This report is for trade waste compliance planning. Discharge limits are subject to the specific Trade Waste Agreement (TWA) issued by SA Water for the site. Excess concentration charges apply per SA Water Fees & Charges Schedule.',
    complianceStatement: 'Effluent quality evaluated against SA Water trade waste discharge conditions. Excess concentration charges (AUD/kg excl. GST) apply when annual volume thresholds are exceeded.',
    dischargeStandardLabel: 'SA Water — Trade Waste Agreement Conditions',
  },

  labels: {
    dischargeLimitsTitle: 'SA Water Trade Waste Limits',
    dischargeLimitsSubtitle: 'SA Water Restricted Wastewater Acceptance Standards (RWAS) — trade waste to sewer',
    complianceTabLabel: 'SA Water Compliance',
    standardBadge: 'SA Water TWA',
  },
};

/* ─── EU UWWTD Standard (Greyed Out) ───────────────────────────────── */
export const EU_UWWTD_STANDARD: WastewaterStandard = {
  code: 'EU_UWWTD',
  name: 'EU Urban Wastewater',
  shortName: 'EU UWWTD',
  country: 'European Union',
  flag: '🇪🇺',
  color: 'text-slate-400',
  bgColor: 'bg-slate-400',
  description:
    'Council Directive 91/271/EEC concerning urban waste-water treatment (UWWTD), as amended by Directive (EU) 2024/... Covering agglomeration > 2000 p.e. and sensitive area discharges.',
  regulatoryBody: 'European Commission / EEA / National Competent Authorities',
  actReference: 'Directive 91/271/EEC (as amended by Directive (EU) 2024/3019) / WFD 2000/60/EC',
  permitType: 'Urban Wastewater Treatment Permit',
  websiteUrl: 'https://environment.ec.europa.eu/topics/water/urban-wastewater-treatment_en',
  available: false,
  comingSoonText: 'Coming Q3 2025',

  limits: {
    // Directive 91/271/EEC Table 1 (secondary treatment)
    // Amended by Directive 2024/3019 (stricter N & P limits)
    tss: 35,          // Table 1: 35 mg/L (90% reduction)
    bod5: 25,         // Table 1: 25 mg/L O2 (70-90% reduction)
    cod: 125,         // Table 1: 125 mg/L O2 (75% reduction)
    oilGrease: 15,    // Typical Member State transposition
    tds: 2000,        // Site-specific
    tkn: 10,          // Sensitive area Table 2
    // 2024 Directive: tighter N and P for >100k PE agglomerations
    totalNitrogen: 6, // 2024 Directive: 6 mg/L N (>100k PE), 10 mg/L (10k-100k PE), 85% removal
    totalPhosphorus: 0.5, // 2024 Directive: 0.5 mg/L P (>100k PE), 1 mg/L (10k-100k PE), 90% removal
    phMin: 6.0,
    phMax: 9.0,
    temperature: 30,  // Receiving water protection
    conductivity: 2500, // Site-specific
    // EQS Directive 2013/39/EU (annual averages)
    lead: 0.5,
    mercury: 0.005,
    cadmium: 0.2,
    chromium: 1.0,
    arsenic: 0.5,
    nickel: 1.0,
    zinc: 5.0,
    copper: 1.0,
    ammoniaN: 5.0,   // 5 mg/L N for salmonid / cyprinid waters
    sulfides: 1.0,
    phenol: 0.5,
    surfactants: 2.0,
    chlorides: 400,
  },

  complianceNotes: [
    'Agglomerations > 2000 p.e. require secondary treatment',
    'Sensitive areas require tertiary nitrogen/phosphorus removal',
    'Monitoring frequencies defined in implementing decisions',
    'Sludge treatment and reuse regulated separately',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Wastewater Assessment Report',
    headerSubtitle: 'Prepared in accordance with EU Urban Waste Water Treatment Directive (91/271/EEC)',
    certificationMark: 'EN ISO',
    certificationText: 'EN ISO standard methods referenced',
    legalReference: 'Council Directive 91/271/EEC (UWWTD), as amended',
    footerText: 'This assessment is for engineering planning purposes. Final discharge requirements subject to national competent authority determination under UWWTD.',
    complianceStatement: 'Effluent quality evaluated against UWWTD requirements. National implementation may impose additional conditions.',
    dischargeStandardLabel: 'EU UWWTD — Directive 91/271/EEC',
  },

  labels: {
    dischargeLimitsTitle: 'EU Discharge Limits',
    dischargeLimitsSubtitle: 'Based on UWWTD 91/271/EEC typical requirements',
    complianceTabLabel: 'EU Compliance',
    standardBadge: 'EU UWWTD Standard',
  },
};

/* ─── US EPA Standard (NPDES / Clean Water Act) ─────────────────────── */
export const US_EPA_STANDARD: WastewaterStandard = {
  code: 'US_EPA',
  name: 'US EPA — NPDES',
  shortName: 'US EPA',
  country: 'United States',
  flag: '🇺🇸',
  color: 'text-red-600',
  bgColor: 'bg-red-600',
  description:
    'US EPA National Pollutant Discharge Elimination System (NPDES) under the Clean Water Act (CWA). Covers point-source discharge permits for municipal and industrial wastewater to US waters. State programs operate under federal delegation.',
  regulatoryBody: 'US EPA / State Environmental Agency (delegated NPDES authority)',
  actReference: 'Clean Water Act (CWA) 33 U.S.C. §1251 et seq. / 40 CFR Parts 122–131',
  permitType: 'NPDES Permit (individual or general)',
  websiteUrl: 'https://www.epa.gov/npdes',
  available: true,

  limits: {
    // US EPA Effluent Guidelines — Secondary Treatment Standards (40 CFR Part 133)
    tss: 30,          // 30-day average: 30 mg/L; 7-day average: 45 mg/L
    bod5: 30,         // 30-day average: 30 mg/L; 7-day average: 45 mg/L
    cod: 150,         // Not a federal secondary standard; typical NPDES permit value
    oilGrease: 10,    // 10 mg/L typical; some states 15 mg/L
    tds: 500,         // Drinking water MCL: 500 mg/L; varies by state
    tkn: 10,          // Nitrogen limits in permits for sensitive receiving waters
    totalNitrogen: 10,// Varies by state; 10 mg/L N total nitrogen
    totalPhosphorus: 1, // 1 mg/L P typical for nutrient-sensitive waters
    phMin: 6.0,       // Typical NPDES range
    phMax: 9.0,
    temperature: 30,  // Thermal discharge limits vary by state
    conductivity: 2000,// State-specific; 2000 µS/cm typical
    // Metals — EPA National Recommended Criteria (aquatic life, freshwater)
    lead: 0.065,      // CMC (acute) criterion; hardness-dependent
    mercury: 0.0007,  // CCC (chronic) criterion — very strict
    cadmium: 0.25,    // CCC freshwater criterion (hardness-dependent)
    chromium: 0.57,   // CCC total chromium
    arsenic: 0.15,    // CCC criterion
    nickel: 0.47,     // CCC criterion (hardness-dependent)
    zinc: 0.12,       // CCC criterion (hardness-dependent)
    copper: 0.009,    // CCC criterion (hardness-dependent) — extremely strict
    ammoniaN: 5.0,    // Varies by pH and temperature; 5 mg/L typical
    sulfides: 0.2,    // Typical NPDES permit
    phenol: 0.3,      // CCC criterion
    surfactants: 2.0, // Typical permit value (MBAS)
    chlorides: 230,   // CCC criterion: 230 mg/L (chronic)
  },

  complianceNotes: [
    'NPDES permit required for all point-source discharges to US navigable waters',
    'Technology-based limits (TBELs) and water quality-based limits (WQBELs) both apply',
    'Secondary treatment required for publicly owned treatment works (POTWs)',
    'Pre-treatment standards apply for industrial discharges to POTW (40 CFR Parts 403–471)',
    'Effluent Limitation Guidelines (ELGs) set technology-based limits by industry category',
    'Anti-degradation policy applies — no degradation of high-quality waters (40 CFR 131.12)',
    'Sampling frequency and reporting requirements specified per permit',
    'State-specific limits may be more stringent than federal minimums',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Wastewater Assessment Report',
    headerSubtitle: 'Prepared in accordance with US EPA NPDES permit requirements (Clean Water Act)',
    certificationMark: 'EPA QA/QC',
    certificationText: 'Analysis methods per 40 CFR Part 136 (EPA-approved methods)',
    legalReference: 'Clean Water Act 33 U.S.C. §1251 et seq. / 40 CFR Parts 122–131',
    footerText: 'This assessment is for engineering planning purposes. NPDES permit limits are set by the permitting authority (US EPA or delegated state agency) based on site-specific receiving water quality and technology standards. Do not use without professional engineering review.',
    complianceStatement: 'Effluent quality evaluated against US EPA NPDES secondary treatment standards and typical permit conditions. State-specific and site-specific permit limits may be more stringent than federal minimums.',
    dischargeStandardLabel: 'US EPA NPDES — Clean Water Act Permit Conditions',
  },

  labels: {
    dischargeLimitsTitle: 'US EPA Discharge Limits',
    dischargeLimitsSubtitle: 'Based on NPDES secondary treatment standards (40 CFR Part 133) and typical permit conditions',
    complianceTabLabel: 'US Compliance',
    standardBadge: 'US EPA NPDES',
  },
};

/* ─── Poland — Wody Polskie (PGW WP) Standard ──────────────────────────
 * Discharge limits per Rozporządzenie Ministra Gospodarki Morskiej i Żeglugi
 * Śródlądowej z dnia 12 lipca 2019 r. w sprawie substancji szczególnie
 * szkodliwych dla środowiska wodnego oraz warunków, jakie należy spełnić przy
 * wprowadzaniu ścieków do wód lub do ziemi (Dz.U. 2019 poz. 1311), which
 * transposes the EU UWWTD. Water permits (pozwolenia wodnoprawne) are issued by
 * Państwowe Gospodarstwo Wodne Wody Polskie through its 11 regional RZGW offices;
 * environmental enforcement is by the regional WIOŚ under GIOŚ.
 * ─────────────────────────────────────────────────────────────────────── */
export const PL_WP_STANDARD: WastewaterStandard = {
  code: 'PL_WP',
  name: 'Poland — Wody Polskie',
  shortName: 'PL Wody Polskie',
  country: 'Poland',
  flag: '🇵🇱',
  color: 'text-red-400',
  bgColor: 'bg-red-600',
  description:
    'Polish water-permit conditions (pozwolenie wodnoprawne) issued by Państwowe Gospodarstwo Wodne Wody Polskie (PGW WP) through its regional RZGW offices. Discharge limits per Rozporządzenie z 12 lipca 2019 r. (Dz.U. 2019 poz. 1311), which transposes the EU Urban Waste Water Treatment Directive. Environmental enforcement by the regional WIOŚ (under GIOŚ).',
  regulatoryBody: 'Państwowe Gospodarstwo Wodne Wody Polskie (PGW WP) — RZGW; enforcement WIOŚ / GIOŚ',
  actReference: 'Prawo wodne (ustawa z 20 lipca 2017 r.) / Rozporządzenie Dz.U. 2019 poz. 1311 / UWWTD 91/271/EEC',
  permitType: 'Pozwolenie wodnoprawne (Water-law permit) / pozwolenie zintegrowane (IPPC)',
  websiteUrl: 'https://www.wody.gov.pl/',
  available: true,

  limits: {
    // Dz.U. 2019 poz. 1311 — typical limits for industrial discharge to waters/ground
    tss: 35,           // zawiesina ogólna
    bod5: 25,          // BZT5
    cod: 125,          // ChZT-Cr
    oilGrease: 15,     // substancje ropopochodne / ekstrakt eterowy (typical)
    tds: 2000,         // site-specific
    tkn: 10,           // azot Kjeldahla (sensitive)
    totalNitrogen: 15, // azot ogólny — 10 mg/L (>100k RLM), 15 mg/L (15k–100k RLM)
    totalPhosphorus: 2,// fosfor ogólny — 1 mg/L (>100k RLM), 2 mg/L (15k–100k RLM)
    phMin: 6.5,        // odczyn pH 6.5–9.0
    phMax: 9.0,
    temperature: 35,   // temperatura ścieków ≤35°C
    conductivity: 2500,// site-specific
    // metale — wartości dopuszczalne (Dz.U. 2019 poz. 1311, załącznik)
    lead: 0.5,
    mercury: 0.06,
    cadmium: 0.4,
    chromium: 0.5,     // chrom ogólny
    arsenic: 0.5,
    nickel: 0.5,
    zinc: 2.0,
    copper: 0.5,
    ammoniaN: 10,      // azot amonowy
    sulfides: 0.2,     // siarczki
    phenol: 0.1,       // fenole lotne
    surfactants: 5.0,  // substancje powierzchniowo czynne (anionowe)
    chlorides: 1000,   // chlorki
  },

  complianceNotes: [
    'Zrzut ścieków do wód lub do ziemi wymaga pozwolenia wodnoprawnego (Wody Polskie / RZGW)',
    'Zrzut do kanalizacji wymaga umowy z przedsiębiorstwem wodno-kanalizacyjnym',
    'Instalacje IPPC wymagają pozwolenia zintegrowanego (BAT/BREF)',
    'Kontrole i pomiary realizuje WIOŚ; sprawozdawczość do bazy KOBiZE / GIOŚ',
    'Opłaty za usługi wodne naliczane przez Wody Polskie wg ustawy Prawo wodne',
    'Obszary wrażliwe (azotanowe OSN) podlegają zaostrzonym limitom N i P',
  ],

  report: {
    headerTitle: 'Reynolds & Bauhm — Ocena gospodarki ściekowej',
    headerSubtitle: 'Sporządzono zgodnie z wymaganiami pozwolenia wodnoprawnego (Wody Polskie)',
    certificationMark: 'PCA / akredytacja',
    certificationText: 'Metody analityczne akredytowane (PCA) lub równoważne',
    legalReference: 'Ustawa Prawo wodne (Dz.U. 2017 poz. 1566) / Rozporządzenie Dz.U. 2019 poz. 1311',
    footerText: 'Niniejsza ocena służy celom planistyczno-inżynierskim. Ostateczne warunki zrzutu określa pozwolenie wodnoprawne wydane przez właściwy RZGW Wód Polskich na podstawie oceny oddziaływania na środowisko wodne.',
    complianceStatement: 'Jakość ścieków oceniono względem typowych warunków pozwolenia wodnoprawnego (Rozporządzenie Dz.U. 2019 poz. 1311). Warunki indywidualne pozwolenia mogą się różnić.',
    dischargeStandardLabel: 'Wody Polskie — warunki pozwolenia wodnoprawnego',
  },

  labels: {
    dischargeLimitsTitle: 'Polskie limity zrzutu',
    dischargeLimitsSubtitle: 'Wg Rozporządzenia Dz.U. 2019 poz. 1311 (transpozycja UWWTD)',
    complianceTabLabel: 'Zgodność PL',
    standardBadge: 'PL Wody Polskie',
  },
};

/* ─── Registry ─────────────────────────────────────────────────────── */
export const ALL_STANDARDS: WastewaterStandard[] = [
  UK_EA_STANDARD,
  AU_EPA_STANDARD,
  SA_WATER_STANDARD,
  EU_UWWTD_STANDARD,
  US_EPA_STANDARD,
  PL_WP_STANDARD,
];

export function getStandard(code: StandardCode): WastewaterStandard {
  const found = ALL_STANDARDS.find((s) => s.code === code);
  if (!found) throw new Error(`Unknown standard: ${code}`);
  return found;
}

export const DEFAULT_STANDARD: StandardCode = 'UK_EA';

/* ─── Convert standard limits to calculator DischargeLimits shape ──── */
export function standardLimitsToCalculator(
  standard: WastewaterStandard
): import('./calculator').DischargeLimits {
  const L = standard.limits;
  return {
    tss: L.tss,
    bod5: L.bod5,
    cod: L.cod,
    oilGrease: L.oilGrease,
    tds: L.tds,
    tkn: L.tkn,
    totalNitrogen: L.totalNitrogen,
    totalPhosphorus: L.totalPhosphorus,
    phMin: L.phMin,
    phMax: L.phMax,
    temperature: L.temperature,
    conductivity: L.conductivity,
  };
}
